import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function DeactivatedAccount() {
  const { user, userProfile, reactivateAccount, signOut } = useAuth();
  const [isReactivating, setIsReactivating] = useState(false);
  const [hasCheckedRedirect, setHasCheckedRedirect] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Éviter les boucles de redirection
    if (hasCheckedRedirect) return;
    
    // Si l'utilisateur n'est pas connecté, rediriger vers login
    if (!user) {
      setHasCheckedRedirect(true);
      navigate('/login', { replace: true });
      return;
    }

    // Si le profil n'est pas encore chargé, attendre
    if (!userProfile) {
      return;
    }

    // Si le compte est actif (ou indéfini), rediriger vers dashboard
    if (userProfile.is_active !== false) {
      setHasCheckedRedirect(true);
      navigate('/dashboard', { replace: true });
      return;
    }

    // Si on arrive ici, le compte est bien désactivé
    setHasCheckedRedirect(true);
  }, [user, userProfile, navigate, hasCheckedRedirect]);

  const handleReactivate = async () => {
    setIsReactivating(true);
    try {
      const { error } = await reactivateAccount();
      if (!error) {
        // Rediriger vers le dashboard après réactivation
        navigate('/dashboard', { replace: true });
      } else {
        console.error('Erreur lors de la réactivation:', error);
        alert('Erreur lors de la réactivation du compte. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur lors de la réactivation:', error);
      alert('Erreur lors de la réactivation du compte. Veuillez réessayer.');
    } finally {
      setIsReactivating(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  // Afficher un loader pendant la vérification initiale
  if (!hasCheckedRedirect || !userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="animate-spin mx-auto h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            <p className="mt-4 text-gray-600">Vérification du statut du compte...</p>
          </div>
        </div>
      </div>
    );
  }

  // Si on arrive ici, on sait que le compte est désactivé
  if (userProfile.is_active !== false) {
    return null; // Ne rien afficher pendant la redirection
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Compte désactivé
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Votre compte a été désactivé le{' '}
              {userProfile.deactivated_at && 
                new Date(userProfile.deactivated_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              }
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Que signifie la désactivation ?
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Votre compte est temporairement désactivé. Vos données sont conservées
                      et vous pouvez réactiver votre compte à tout moment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleReactivate}
                disabled={isReactivating}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isReactivating ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Réactivation en cours...
                  </div>
                ) : (
                  'Réactiver mon compte'
                )}
              </button>

              <button
                onClick={handleSignOut}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
